import { chatConfig } from "../../config/chatConfig";
import { ACCEPTED_FILE_TYPES } from "../../config/AcceptedFileTypes";
import { ConvexMessage, ImageType, MessageDocument } from "../../types/message";
import { TextFileRecord, ImageFileRecord } from "./types";
import { AttachmentType } from "../../types/message";

export const countTotalAttachments = (messages: ConvexMessage[] = [], isSpace: boolean = false, spaceAttachments: MessageDocument[] = []): number => {
    if (isSpace) {
      return spaceAttachments.length;
    }
    return messages.reduce((count, message) => {
      let fileCount = 0;
  
      if (message.textAttachments) {
        fileCount += Array.isArray(message.textAttachments) ? message.textAttachments.length : 0;
      }
  
      if (message.images) {
        fileCount += Array.isArray(message.images) ? message.images.length : 0;
      }
  
      return count + fileCount;
    }, 0);
};

export const extractContent = (content: string): string => {
    const blockTypes = [
      { start: '<PDF START>', end: '<PDF END>' },
      { start: '<DOC START>', end: '<DOC END>' },
      { start: '<CODE START>', end: '<CODE END>' },
    ];

    for (const { start, end } of blockTypes) {
      const startIdx = content.indexOf(start);
      const endIdx = content.indexOf(end);
      if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
        const contentStart = content.indexOf('\n', startIdx) + 1;
        const contentEnd = endIdx;
        return content.slice(contentStart, contentEnd).trim();
      }
    }
    return content;
}

export const generateFileId = (file: File): string =>
    `${file.name}-${file.size}-${Date.now()}`;

export const getFileCategory = (file: File): AttachmentType | null => {
    // Always check by extension first for code/text files
    const codeExtensions = [
      '.py', '.js', '.ts', '.jsx', '.tsx', '.java', '.c', '.cpp', '.cs', '.rb', '.go', '.php', '.rs', '.swift', '.kt', '.m', '.scala', '.sh', '.pl', '.lua', '.r', '.dart', '.html', '.css', '.json', '.xml', '.yml', '.yaml', '.sql', '.svg'
    ];
    const lowerName = file.name.toLowerCase();

    // If the file extension matches a code file, return 'code'
    if (codeExtensions.some(ext => lowerName.endsWith(ext))) {
      return 'code';
    }

    // Accept images (only png and jpeg) as images
    if (file.type === 'image/png' || file.type === 'image/jpeg' || file.type === 'image/jpg') {
      return file.type;
    }

    // Treat SVGs as code files regardless of mime
    if (lowerName.endsWith('.svg') || file.type === 'image/svg+xml') {
      return 'code';
    }

    // Accept PDFs
    if (file.type === 'application/pdf') return 'pdf';

    // All backend-accepted document MIME types → 'doc'
    const docMimeTypes = [
      'text/plain',
      'text/markdown',
      'text/csv',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/rtf',
      'application/vnd.oasis.opendocument.text',
      'application/vnd.oasis.opendocument.spreadsheet',
      'application/vnd.oasis.opendocument.presentation',
      'application/vnd.apple.pages',
      'application/vnd.google-apps.document',
      'application/vnd.google-apps.spreadsheet',
      'application/vnd.google-apps.presentation',
      'application/x-abiword',
    ];
    if (docMimeTypes.includes(file.type)) {
      return 'doc';
    }

    // Extension-based doc fallbacks
    const docExtensions = ['.md', '.csv', '.rtf', '.odt', '.ods', '.odp', '.pages', '.abw', '.xls', '.xlsx', '.ppt', '.pptx', '.doc'];
    if (docExtensions.some(ext => lowerName.endsWith(ext))) {
      return 'doc';
    }

    // Accept octet-stream and empty type as 'code' if extension matches
    if (
      (file.type === 'application/octet-stream' || file.type === '') &&
      codeExtensions.some(ext => lowerName.endsWith(ext))
    ) {
      return 'code';
    }

    // Fallback: check if file type is in accepted types (for future extensibility)
    const acceptedTypes = Object.keys(ACCEPTED_FILE_TYPES);
    const isAccepted = acceptedTypes.some((acceptedType) => {
      if (acceptedType.endsWith('/*')) {
        const [baseType] = acceptedType.split('/');
        return file.type.startsWith(baseType);
      }
      return file.type === acceptedType;
    });

    if (!isAccepted) return null;

    // If we get here, we don't know the category
    return null;
};
export const getCodeLanguageByExtension = (extension: string): string => {
  // Normalize extension to start with dot and lowercase
  let ext = extension.trim().toLowerCase();
  if (!ext.startsWith('.')) {
    ext = '.' + ext;
  }

  // Extract only the last extension (e.g., for "foo.types.ts" get ".ts")
  const lastDotIndex = ext.lastIndexOf('.');
  if (lastDotIndex !== -1 && lastDotIndex !== 0) {
    ext = ext.slice(lastDotIndex);
  }

  // Map extensions to language names for syntax highlighter
  const extensionToLanguage: { [key: string]: string } = {
    '.py': 'python',
    '.js': 'javascript',
    '.ts': 'typescript',
    '.jsx': 'jsx',
    '.tsx': 'tsx',
    '.java': 'java',
    '.c': 'c',
    '.cpp': 'cpp',
    '.cs': 'csharp',
    '.rb': 'ruby',
    '.go': 'go',
    '.php': 'php',
    '.rs': 'rust',
    '.swift': 'swift',
    '.kt': 'kotlin',
    '.m': 'objectivec',
    '.scala': 'scala',
    '.sh': 'bash',
    '.pl': 'perl',
    '.lua': 'lua',
    '.r': 'r',
    '.dart': 'dart',
    '.html': 'html',
    '.css': 'css',
    '.json': 'json',
    '.xml': 'xml',
    '.yml': 'yaml',
    '.yaml': 'yaml',
    '.sql': 'sql',
    '.md': 'markdown',
    '.svg': 'xml'
  };

  const language = extensionToLanguage[ext] ? extensionToLanguage[ext] : (ext.startsWith('.') ? ext.slice(1) : 'text');
  return language;
};

export function checkIfFileIsAccepted(file: File): boolean {
  const fileSizeInMB = file.size / (1024 * 1024);
  if (fileSizeInMB > chatConfig.limits.files.MAX_FILE_SIZE_MB) {
    return false;
  }

  const lowerName = file.name.toLowerCase();

  const isAccepted = Object.entries(ACCEPTED_FILE_TYPES).some(([mimeType, extensions]) => {
    if (mimeType.endsWith('/*')) {
      const [baseType] = mimeType.split('/');
      if (file.type.startsWith(baseType)) {
        return true;
      }
    } else if (file.type === mimeType) {
      return true;
    }

    return extensions.some((ext) => lowerName.endsWith(ext));
  });

  return isAccepted;
}

export function checkIfFileIsDuplicate(
  file: File,
  textFiles: TextFileRecord[],
  imageFiles: ImageFileRecord[]
): boolean {
  const category = getFileCategory(file);
  if (category === 'image/png' || category === 'image/jpeg' || category === 'image/jpg') {
    return imageFiles.some(
      (existingFile) =>
        existingFile.name === file.name &&
        existingFile.size === file.size
    );
  } else {
    return textFiles.some(
      (existingFile) =>
        existingFile.name === file.name &&
        existingFile.size === file.size
    );
  }
}

export function getImageType(file: File): ImageType | null {
  const imageTypes = ['image/png', 'image/jpeg', 'image/jpg'];
  if (imageTypes.includes(file.type)) {
    return file.type as ImageType;
  }

  return null;
}
